/**
 * src/data/sessions.js — THIN AGGREGATOR (959 lines, was 5070)
 * ─────────────────────────────────────────────────────────────────────────────
 * Imports from 4 source files and re-exports as unified SESSIONS array.
 * All components import from THIS file only — never from source files directly.
 *
 * HOW TO USE:
 *   import { SESSIONS, PROJECT_RULES, PRE_SESSION, END_SESSION } from '../data/sessions';
 *
 * HOW TO UPDATE SESSIONS (never edit SESSIONS here — edit the correct source):
 *   Session completed      → move entry to sessions-archive.js (ARCHIVE_SESSIONS)
 *   Active / next session  → edit sessions-active.js (ACTIVE_SESSIONS)
 *   Approved + sequenced   → add to sessions-pipeline.js (PIPELINE_SESSIONS)
 *   Vision / unscheduled   → add to sessions-future.js (FUTURE_SESSIONS)
 *
 * Rule 11 — New feature sequence:
 *   Capture in FeatureRequestsTab → get approval → add to sessions-pipeline.js
 *   with status: 'future' → assign session number → promote to active → write code.
 *
 * STATIC EXPORTS IN THIS FILE (never move these to source files):
 *   PROJECT_RULES · LEGAL_RULES · NEVER_DO · PROJECT_SCOPE
 *   PRE_SESSION · END_SESSION · SCENARIO_MATRIX
 *   D3_SIGNAL_UNIVERSE · D3_SIGNAL_CHAIN · VENDOR_REGISTRY
 *   getSessionById · getCurrentSession · getDoneSessions · SESSION_PROGRESS
 *
 * Last updated: May 31, 2026 — Phase structure resequenced (Phase 0-12 + Monetise) · SIGNAL-DECOUPLE + DOC-SYNC-2 done
 * ─────────────────────────────────────────────────────────────────────────────
 */


// ─── IMPORTS — must be at top per ESLint import/first rule ──────────────────
import { ARCHIVE_SESSIONS }  from './sessions-archive';
import { ACTIVE_SESSIONS }   from './sessions-active';
import { PIPELINE_SESSIONS } from './sessions-pipeline';
import { FUTURE_SESSIONS }   from './sessions-future';

// ─── PROJECT RULES ────────────────────────────────────────────────────────────
// Source: HANDOVER.md Section 0 Rules 1–11, Section 2 Rules 1–7, Section 3 Legal
// Displayed in RoadmapPage Rules tab. Enforced in session gate (prereqs).

export const PROJECT_RULES = [
  { id: 1,  category: 'code',    rule: 'Read files completely before patching — no grep guessing, no assumptions' },
  { id: 2,  category: 'code',    rule: 'One fix at a time — confirm working on live site before moving to next' },
  { id: 3,  category: 'build',   rule: 'Build before every commit — npm run build must be green, zero ESLint errors' },
  { id: 4,  category: 'build',   rule: 'Never commit broken build to main — Cloudflare CI=true treats warnings as errors' },
  { id: 5,  category: 'git',     rule: 'Deploy sequence (EXACT): bash scripts/smoke-test.sh (110/110 green) → CI=true npm run build (clean) → bash tasks/build-verify.sh (bundle safe) → git add -A → commit → push dev → merge main → push main → bash tasks/post-deploy-check.sh (site live) → checkout dev' },
  { id: 6,  category: 'legal',   rule: 'Signal language: BULLISH / NEUTRAL / BEARISH only — NEVER BUY / SELL / WAIT / HOLD' },
  { id: 7,  category: 'style',   rule: 'Inline styles only — no CSS files, no Tailwind, no styled-components, no external style constants. All styles live in the component file itself.' },
  { id: 8,  category: 'code',    rule: 'Python3 heredoc for all file writes in bash — NEVER cat<<EOF or echo >' },
  { id: 9,  category: 'process', rule: 'Check sessions.js (HANDOVER Section 14 points here) at session start for current task and prereqs' },
  { id: 10, category: 'process', rule: 'Update sessions-active.js + HANDOVER.md + SESSION.md + CLAUDE.md + CHANGELOG.md at end of every session — set status done, add date+commit, set next session status to next. BRIEF.md retired April 2026.' },
  { id: 11, category: 'process', rule: 'New features: add to sessions.js here first → document in HANDOVER → get approval → assign sessionNumber → write code. No exceptions.' },
  { id: 12, category: 'build',   rule: 'Smoke test before EVERY commit — bash scripts/smoke-test.sh must be 100% green (currently 125/125). Never commit if any check fails. This is non-negotiable and applies in every new chat and every session without exception.' },
  { id: 13, category: 'code',    rule: 'Read the live file before patching — cat the actual file in the current session. Never patch from memory or a previous view from any prior session.' },
  { id: 14, category: 'process', rule: 'Scripts folder: all task/patch/utility bash scripts go in scripts/ — never in repo root, never in /tmp. Commit them with the project.' },
  { id: 15, category: 'code',    rule: 'Vendor-agnostic endpoints: every backend route uses our own naming (/api/fundamentals/:ticker not /api/fmp/:ticker). Swap vendors in one file without touching frontend.' },
  { id: 16, category: 'process', rule: 'IBKR execution compatibility: every signal function and scoring algorithm must be designed so the D3 trading engine can consume it. Signal display: BULLISH/NEUTRAL/BEARISH. Signal engine: PROCEED/HOLD/ABORT. This applies from today, not Phase D.' },
  { id: 17, category: 'process', rule: 'Every session = guest feature + Super Admin equivalent. Never build a guest feature without its personal suite counterpart in the same commit.' },
  { id: 18, category: 'process', rule: 'End of every session: update SESSION.md + CLAUDE.md + CHANGELOG.md in addition to HANDOVER.md + sessions-active.js. BRIEF.md retired April 2026 — replaced by SESSION.md. These 5 files must always be in sync. Non-negotiable.' },
  { id: 20, category: 'product',  rule: 'D3 CONTRIBUTION MANDATORY (Rule 20): Before building ANY feature ask these 5 questions: (1) Does this generate ad/subscription revenue to fund the $210-500/mo infrastructure cost? (2) Does this improve D3 signal quality for any of the 9 strategies (A=momentum · B=speculation · C=pumpDump · D=shorting · E=value · F=shortSqueeze · G=insiderCluster · H=consolidationBreakout · I=fraudInvestigation)? (3) Does this generate proprietary crowd signal data that no vendor sells — member watchlists, search patterns, calculator usage? (4) Does this help the 24/7 autonomous engine run without human supervision? (5) Does this improve the self-learning loop (log → analyse → improve)? If the answer to ALL FIVE is NO — this is a distraction. Deprioritise without exception. This rule exists because product costs must be covered by MarketPulze revenue, not trading P&L.' },
  { id: 19, category: 'process', rule: 'Sync rule: at end of every session update ALL 6 sync targets in order: (1) sessions-active.js (2) HANDOVER.md (3) SESSION.md (4) CLAUDE.md (5) CHANGELOG.md (6) CLAUDE_SESSION_PROTOCOL.md — update Current Session + Last Commit + Session History. Paste CLAUDE_SESSION_PROTOCOL.md to Claude Project Instructions after committing. Every new feature adds SCENARIO_MATRIX entries. Smoke test count must go UP each session.' },
  // Rule 21 — Session Delivery Standard
  { id: 21, category: 'process', rule: 'SESSION DELIVERY (E13 — mandatory): All files output to /mnt/user-data/outputs/ with FLAT filenames (no path encoding). Every session produces tasks/apply-SXXX.sh containing FILE_MAP (flat-name → repo-path), COMMIT_MSG, SMOKE_ADDITIONS. User drags all files into _staging/ and runs bash _staging/apply-SXXX.sh. One command: validates → moves (mv not cp) → smoke → build → deploy. Template: tasks/apply-session-template.sh. Never give manual copy instructions. Never use cp — always mv.' },
  { id: 22, category: 'process', rule: 'BRANCH GUARD (mandatory): git checkout dev is the FIRST step before dragging any file into _staging/. Every apply script aborts immediately with a clear error if not on dev branch. Committing from main skips the Cloudflare dev→Preview build and breaks the deploy audit trail. The template enforces this. No exceptions.' },
  { id: 23, category: 'process', rule: 'COMMIT TYPE SYSTEM (Rule 23 — enforced in apply scripts): Every apply script declares SESSION_TYPE. Claude sets this based on commit prefix. 6 types: Type 0 (session start — no commit, Claude outputs 7-file checklist before any response, hard stop if any missing). Type 1 feat:/SESSION_TYPE=full — all 7 sync files mandatory, hard abort if missing, smoke count must reflect new checks. Type 2 fix:/SESSION_TYPE=hotfix — only changed files required, smoke always runs, count changes if new check added. Type 3 docs:plan/SESSION_TYPE=plan — sessions.js only, smoke runs. Type 4 docs:sync/SESSION_TYPE=sync — only changed sync files, hard abort on mismatch. Type 5 chore:/SESSION_TYPE=infra — no sync files, smoke runs. Smoke and build run on ALL types without exception. Smoke count changes based on what was added, not commit type.' }
];

export const LEGAL_RULES = [
  { id: 'L1',  rule: 'ALWAYS show "Data delayed 15 min" to guests on every price display. NEVER say "real-time" to guests.' },
  { id: 'L2',  rule: 'NEVER say "refreshes every X min" to anyone — implies trading advantage, legal risk.' },
  { id: 'L3',  rule: 'NEVER use Yahoo Finance as data source shown to users — ToS: personal use only, never redistributed.' },
  { id: 'L4',  rule: 'Polygon.io — PERMANENTLY BANNED. ToS violation. Never re-add for any reason.' },
  { id: 'L5',  rule: 'FMP — PAUSED. Meeting held Mar 16 2026, quote $8,925/yr declined. Do not integrate. Revisit when ad revenue > $200/mo or subscription MRR > $500/mo. Contact: michael@financialmodelingprep.com when conditions met.' },
  { id: 'L6',  rule: 'Crypto tab: prices and % change only — NO signals ever. Label: "Not investment advice".' },
  { id: 'L7',  rule: 'Rates/Commodities/Forex tab: display only — NO signals ever. FCA/CFTC regulatory risk.' },
  { id: 'L8',  rule: 'Economic calendar events: NO AI signals on macro events — regulatory risk.' },
  { id: 'L9',  rule: 'NEVER send digest without digestOptIn=true AND digestOptInAt != null. Never default digestOptIn to true.' },
  { id: 'L10', rule: '"Not investment advice" required on every analysis page, share card watermark, and alert email.' },
  { id: 'L11', rule: '"Past performance does not guarantee future results" required on calculator page.' },
  { id: 'L12', rule: 'NEVER open public registration — invite-only is permanent strategy (configurable only via Admin Settings beta_open).' },
  { id: 'L13', rule: 'Every data vendor displayed to ANY user (guest or member) must have confirmed commercial display licence. Free tier = dev/personal use only. Never ship a feature displaying vendor data to users without confirming their commercial ToS first.' },
];

export const NEVER_DO = [
  'Suggest TypeScript, Next.js, Vite, or any framework migration',
  'Write duplicate keys in React style objects (e.g. two border: entries) — ESLint no-dupe-keys fails Cloudflare CI=true',
  'Run npm run build without CI=true — always CI=true npm run build to match Cloudflare environment',
  'Write smoke check() with ! grep -q negation in eval — use test $(grep -c X file) -eq 0 instead',
  'Add npm packages without asking — keep dependencies minimal',
  'Rewrite a working file "to clean it up" — only fix confirmed bugs',
  'Use localStorage anywhere in frontend code — use MongoDB state instead',
  'Put secrets in code — always process.env.VAR_NAME',
  'Push directly to main branch — always dev → merge → main',
  'Use HTML <form> tags — use onClick/onChange handlers only',
  'Use CSS files, Tailwind classes, CSS modules, or external style constants — ALL styles inline in the component file',
  'Use BUY / SELL / WAIT / HOLD language anywhere in any file',
  'Remove app.set("trust proxy", 1) from backend/server.js — breaks OCI VM-1 IP rate limiting and Cloudflare IP detection (was Railway — migrated to OCI VM-1 May 4, 2026)',
  'Change authenticateToken signature in backend/middleware/auth.js — used everywhere',
  'Remove /admin or /profile from public/robots.txt — security',
  'Use Math.random() in any scoring or signal logic — non-deterministic',
  'Serve Stooq data to users — internal/dev use only (ToS: personal/research only)',
  'Cancel any membership before Phase C confidence — memberships stay ACTIVE until replacement is near-competitive, not just skeleton built',
  'Commit without running smoke-test.sh first — the deploy sequence is a single chained command',
  'Use string replacement to patch React component files larger than 50 lines — write the complete file using Python heredoc instead',
  'Use Python errors=surrogatepass on production React files — use errors=replace or write complete clean content',
  'Run bash _staging/apply-SXXX.sh while on main branch — always git checkout dev first (Rule 22)',
  'Output files with path-encoded names like src-pages-File.jsx — always flat names in outputs',
  'Give manual copy instructions like "copy this to backend/routes/" — produce tasks/apply-SXXX.sh',
  'Use cp in apply scripts — always mv so _staging/ empties and nothing duplicates',
  'Set SESSION_TYPE incorrectly — feat:/docs: = full, fix: = hotfix, docs:plan = plan, docs:sync = sync, chore: = infra',
  'Skip SESSION_TYPE in an apply script — every apply script must declare SESSION_TYPE',
  'Omit sync validator cross-check on SESSION_TYPE=full or SESSION_TYPE=sync commits',
  'Push to main without running bash tasks/build-verify.sh after npm run build',
  'Push to main without running bash tasks/post-deploy-check.sh to confirm site is live',
];


// ─── PROJECT SCOPE ──────────────────────────────────────────────────────────────────────────
// Machine-readable companion to HANDOVER.md Section 00.
// Read by RoadmapPage.jsx Project Scope tab.
// A new Claude session reading this file gets full product context without re-explanation.
// Update this whenever product strategy, vendor decisions, or scope changes.

export const PROJECT_SCOPE = {

  product: {
    identity: 'Personal trading ecosystem. NOT a stock signal website.',
    endGoal: 'IBKR automated trading engine (D3). Every feature must be execution-compatible.',
    legalBoundary: 'Personal use only. Single account. Single owner. Every trade logged with Claude reasoning — never purged.',
    monetisationTimeline: '1-2 years away. Build personal tool first. Then guest+ads. Then subscriptions.',
  },

  // Added June 2026 — four-product hierarchy was documented in PRODUCT_COMPASS.md/CLAUDE.md/
  // D3_CORE.md (backend Claude-facing docs) but had never been added to this user-facing data
  // file. Sourced from MarketPulze_Product_Strategy.docx (May 2026) + those .md files.
  // Owner-confirmed caveat: the source docx does NOT cover all planned Product 2 scope — treat
  // this as the best documented picture so far, not a final/complete spec. The docx's
  // "Five-Tier Storage Architecture" table is deliberately NOT reproduced here — it lists
  // edgar_events/edgar_insider/congressional_trades under OCI NoSQL, superseded by the May 2026
  // Oracle SQL migration (see ARCH.md "CONGRESSIONAL DATA" section / DB_SCHEMA.md).
  fourProducts: {
    twoEqualMasters: 'Screener (human) + D3 (machine) both serve from the same data layer equally — Product 1 is not secondary to D3. If Product 1 fails, D3 has no signal foundation.',
    products: [
      {
        n: 1, name: 'AI-Powered Screener Site', status: 'BUILDING NOW', access: 'Guest + Member + Admin',
        summary: 'Subscriptions + Tier 3 signal harvest. Replaces Finviz, TradingView, Benzinga Pro, Unusual Whales, Morningstar, Market Chameleon, Scanz.',
      },
      {
        n: 2, name: 'Autonomous D3 Trading Engine', status: 'Owner only · private · VM-2', access: 'Super Admin / Owner only',
        summary: '7-layer LLM engine (L1-L7). Real capital, no human intervention in normal operation. Reads same data as screener, acts autonomously on proven strategy. trading.marketpulze.com — JWT + TOTP + isSuperAdmin, private subnet, no public IP, no CDN caching, Bastion SSH only.',
      },
      {
        n: 3, name: 'Inner Circle Portal', status: 'Phase D+ · after D3 proven 12+ months', access: 'Paid subscribers (future)',
        summary: 'Truncated directional signals, 15-30min delayed vs owner. No positions, P&L, or conviction scores. "The truncation IS the protection — anyone with the full engine could replicate D3, anyone with a 15-minute delayed directional feed cannot."',
      },
      {
        n: 4, name: 'Commercial Truncated Engine', status: 'Phase D+ · future', access: 'Paid subscribers, own broker (future)',
        summary: 'Members connect their own broker account. D3 provides signals only — MarketPulze never touches member capital, intelligence layer only. Requires: D3 live 12+ months, Inner Circle validated, legal review complete, both broker adapters tested.',
      },
    ],
    sevenLayerEngine: [
      { l: 'L1', name: 'Signal Scoring', model: 'Llama 4 Scout', detail: 'Every 5min during market hours. >70 BULLISH or <30 BEARISH triggers L2.' },
      { l: 'L2', name: 'Pattern Classification', model: 'Gemini 2.5 Flash', detail: 'Confidence >0.75 triggers L3.' },
      { l: 'L3', name: 'Deep Reasoning', model: 'Llama 4 Maverick', detail: 'Multi-pillar reasoning. Fires only when 3+ inflections align (see below). Outputs full thesis.' },
      { l: 'L4', name: 'Cross-Validation', model: 'gpt-oss-120b', detail: 'Independent verification of L3 only. OCI-7B-EVAL required before any capital role — NOT yet evaluated, see OCI_7B_EVAL.md.' },
      { l: 'L5', name: 'Capital Gate', model: 'Claude Sonnet', detail: 'FINAL VETO, independent of OCI infrastructure. Triple consensus required: L3+L4+L5 all PROCEED.' },
      { l: 'L6', name: 'Self-Improvement', model: 'Llama 4 Maverick', detail: 'Weekly, Sunday 2am. Proposes weight changes. Owner approves EACH one. Never auto-applies.' },
      { l: 'L7', name: 'Summaries', model: 'Claude Sonnet', detail: 'Post-trade summary, performance attribution, member-facing AI brief.' },
    ],
    sixInflections: [
      'Earnings Revision Momentum — analyst clusters + PEAD',
      'Post-Event Drift — PDUFA · earnings · gov contracts · M&A',
      'Forced Capital Movement — index inclusion/exclusion · short squeeze',
      'Government Money Flow — USASpending contracts + IRA subsidies + DoD',
      'Corporate Structure Change — M&A arb · spin-off · uplisting',
      'Macro Regime Shift — FRED rates/CPI · congressional rotation signal',
    ],
    inflectionsNote: 'L3 fires only when 3+ of these 6 inflections align. UNCONFIRMED relationship to D3_INTELLIGENCE_FRAMEWORK.md\'s "five analytical dimensions" (propagation velocity, silence as signal, rotation direction, network depth, historical performance) — current working theory is inflections = what triggers L3, dimensions = how L3 scores conviction once triggered, but this has not been confirmed against the owner\'s intent. Flagged, not resolved.',
    ownerPortalFeatures: [
      'Live positions and P&L (D3c onwards)',
      'Full signal stack — L1-L5 outputs, L6 proposed weight changes, L7 portfolio state',
      'L3 full reasoning text for every open position (why D3 entered, what it expects)',
      'L6 weight change proposals — owner reviews and approves or rejects each one',
      'Kill switch — halt all trading, close all positions. TOTP re-confirm + 5-second countdown',
      'Manual override — enter or exit position outside D3 conviction',
      'Broker connection status per adapter (IBKR, ONDO, others)',
      'Performance vs manual trading comparison',
      'DR status panel: Redis health, ATP-2 sync lag, VM-2 heartbeat',
    ],
    whatStaysPrivate: [
      'L6 weight values and formula',
      'trade_log full records (entry, exit, P&L, full reasoning)',
      'conviction_score computation',
      'l6_weight_history (audit trail)',
      'mp-vault-d3 IBKR credentials',
      'VM-2 itself — no public IP, private subnet, Bastion access only',
      'D3 execution code — separate private GitHub repo, never in main repo',
    ],
    brokerAdapter: {
      rule: 'D3 engine NEVER calls any broker API directly. Always via: brokerAdapter.placeOrder(order) — permanent, Rule R33.',
      adapters: ['IbkrAdapter — routes to IB Gateway on VM-2 (current primary)', 'OndoAdapter — future ONDO adapter file'],
      note: 'Adding any new broker = one new adapter file, zero D3 engine changes. D3 learns strategies; the adapter handles execution syntax. Permanently separated.',
    },
    privacyBoundaries: [
      { capability: 'Signal columns (BULLISH/BEARISH)', p1: 'truncated', p3: 'delayed 15-30min', p2: 'full' },
      { capability: 'Insider trade display', p1: 'Form 4 buys ≥$50K', p3: 'same display', p2: 'full signal + cluster' },
      { capability: 'Congressional display', p1: 'normalised', p3: 'direction only', p2: 'full rotation' },
      { capability: 'Conviction scores', p1: '✗', p3: '✗', p2: '✓' },
      { capability: 'L3 reasoning text', p1: '✗', p3: '✗', p2: '✓' },
      { capability: 'Positions / P&L', p1: '✗', p3: '✗', p2: '✓' },
      { capability: 'L6 weight proposals', p1: '✗', p3: '✗', p2: '✓ approve/reject' },
      { capability: 'Kill switch', p1: '✗', p3: '✗', p2: '✓' },
      { capability: 'Broker adapter status', p1: '✗', p3: '✗', p2: '✓' },
      { capability: 'Trade history detail', p1: '✗', p3: '✗', p2: '✓ full' },
      { capability: 'Performance summary', p1: '✗', p3: '✓ aggregate only', p2: '✓ full' },
    ],
    sourceNote: 'Sourced from MarketPulze_Product_Strategy.docx (May 2026) + PRODUCT_COMPASS.md + D3_CORE.md, added to this file June 2026. Owner-confirmed: source docx does not cover all planned Product 2 scope — validate against current intent before treating as final.',
  },

  threeStages: [
    {
      stage: 1, name: 'Personal Tool',
      goal: 'Replace all 8 paid memberships. Tool saves $401/mo before first user registers.',
      trigger: 'Each session cancels one subscription when its replacement feature is live and validated.',
      status: 'IN PROGRESS',
    },
    {
      stage: 2, name: 'Guest + Ads',
      goal: 'SEO traffic. Ad revenue covers infrastructure. Ads on guest pages ONLY — never /admin /roadmap /trading.',
      trigger: 'After S25 complete + AdSense approved. Target: $100/mo ads covers Phase B/C costs.',
      status: 'PLANNED — after S25',
    },
    {
      stage: 3, name: 'Subscriptions',
      goal: 'Monetise proven product with proven audience.',
      trigger: '500+ active members + 5,000+ monthly guests + $100/mo ad revenue + Phase C done + 6 months signal accuracy data.',
      tiers: ['Member $0 (invite-only)', 'Pro $9/mo', 'Premium $29/mo', 'Elite $49/mo'],
      status: 'FUTURE — 1-2 years',
    },
  ],

  membershipReplacement: [
    { platform: 'Finviz Elite',         costPerMonth: 24.96,  cancelAfter: 'Screener + insider live',      session: 'S27D' },
    { platform: 'Scanz',               costPerMonth: 197.00, cancelAfter: 'Alert builder + scanner live', session: 'S25' },
    { platform: 'Benzinga Pro',        costPerMonth: 37.00,  cancelAfter: 'News + calendar live',          session: 'S23B + S27A' },
    { platform: 'Unusual Whales',      costPerMonth: 48.00,  cancelAfter: 'Congressional tracker live',   session: 'S27D' },
    { platform: 'TradingView',         costPerMonth: 15.00,  cancelAfter: 'Candlestick chart live',        session: 'S27E' },
    { platform: 'Motley Fool',         costPerMonth: 16.58,  cancelAfter: 'Signal tracker live',           session: 'S26' },
    { platform: 'Morningstar',         costPerMonth: 20.75,  cancelAfter: 'AI Scorecard live',             session: 'S28D' },
    { platform: 'Cramer Action Alerts',costPerMonth: 41.67,  cancelAfter: 'AI Morning Brief live',        session: 'S25' },
  ],

  userTiers: [
    {
      tier: 'Guest',
      access: 'Partial features. Enough to be impressed, not satisfied.',
      revenueRole: 'Ad impressions. SEO traffic. Conversion funnel.',
      authCode: 'No auth. Public routes.',
      hookDesign: 'Every page has one deliberate visible lock. CTA is always "Request Invite". Never hide locks — visible locks create desire.',
    },
    {
      tier: 'Invite-Member',
      access: 'Full features. Quality-vetted early adopters. Daily retention loop.',
      revenueRole: 'Conversion proof. Future subscription base.',
      authCode: 'JWT required. authenticateToken middleware.',
    },
    {
      tier: 'Admin',
      access: 'User/request/tester/settings management. No trading suite access.',
      revenueRole: 'Operational only.',
      authCode: 'requireAdmin. Settings.admins[] in MongoDB — not hardcoded.',
    },
    {
      tier: 'Super Admin',
      access: 'Everything. Personal trading suite (/trading/*). IBKR execution engine. Priority tier.',
      revenueRole: 'Product owner. Personal tool. The reason this product exists.',
      authCode: 'SUPER_ADMIN_EMAILS env var ONLY. NEVER stored in DB. NEVER in code.',
    },
  ],

  guestHookRules: [
    'Never give guests the complete version of any feature',
    'Every guest page has one deliberate visible lock that creates desire for membership',
    'CTA is always "Request Invite" or "Request Early Access" — never generic "Sign Up"',
    'Show the lock — never hide it. Visible locks create desire.',
    '/stocks/:ticker — AI teaser 2 lines only. Full breakdown is the hook.',
    '/screener — top 10 results only. "Join to see all 847 results" is the hook.',
    '/calendar — My Watchlist filter locked. AI signal pills on earnings locked.',
    '/news — My Watchlist filter locked. Trending sidebar visible.',
  ],

  ibkrMemory: {
    importance: 'IBKR automated trading is the technical end goal of the entire product. Applies to EVERY coding session from today.',
    rule: 'Every signal function, scoring algorithm, and analysis output must be designed for execution compatibility with the D3 trading engine.',
    signalDisplay: 'BULLISH / NEUTRAL / BEARISH',
    signalEngine: 'PROCEED_LONG / PROCEED_SHORT / PROCEED_HEDGE / ABORT — bidirectional, all market conditions',
    noDeterminism: 'No Math.random() in any scoring or signal logic — already in NEVER_DO.',
    auditTrail: 'Every signal permanently logged with full context (ticker, score, data source, timestamp). Never purged.',
    dependencyChain: [
      'Phase C complete (AI Scorecard, full depth, screener)',
      'D0 live — personal suite used manually 60+ days with documented positive Sharpe',
      'D3-Infra — OCI VM + IB Gateway port 4001 + Redis installed and running',
      'D3a — shadow mode 30 days, paper accuracy > 65%',
      'D3b — Alpaca paper trading 60 days, Sharpe > 0.8, drawdown < 10%',
      'D3c — IBKR live 5% capital 90 days',
      'D3d — full expansion',
    ],
    currentBlocker: 'OCI Oracle signup ticket open. D3 cannot start without OCI. IB Gateway needs 24/7 persistent server.',
    stooqArchitecture: {
      note: 'Stooq free historical data — download and load into OCI ATP (NOT MongoDB — too large)',
      ociFree: 'OCI ATP 20GB free — stores: stooq_us_daily(506MB) + stooq_world_daily(181MB) + stooq_macro(0.9MB)',
      worldMarketsD3: 'Nikkei/FTSE/DAX open before US — world market overnight data feeds pre-market D3 gate',
      etfDataChain: 'Finnhub live → AV GLOBAL_QUOTE (seed) → Tiingo EOD → MongoDB last known → OCI ATP historical',
      marketstackNote: 'Phase B evaluation only — pause until $200/mo revenue. Evaluate endpoints + commercial licence then.',
    },
    legalBoundary: 'Personal use only. Single account. Single owner. No RIA registration needed.',
    d3StrategyUniverse: {
      momentum:    'Day trading — trend + volume + RSI + VWAP. Same-day exit. 1-2% risk/trade.',
      speculation: 'News catalyst — breaking news + earnings + FDA + M&A. Hours to days. 0.5-1% risk.',
      pumpDump:    'Volume anomaly + social spike + float < 50M + no legit news. 0.5% MAX risk. LEGAL: identify pattern, never participate in manipulation.',
      shorting:    'Momentum breakdown + fundamentals + put flow + insider selling. Days to weeks. 1-2% risk.',
      value:                 'PE below sector + revenue growth + insider buying + 52W low. Weeks to months. 3-5% risk.',
      shortSqueeze:          'High short float >10% + institutional accumulation >80% + good fundamentals = 3 sub-modes: long accumulation (Mode 1), squeeze trigger (Mode 2), contrarian short (Mode 3). Holding: days to months.',
      insiderCluster:        '3+ distinct insiders open-market buying within 30 days = CLUSTER_STRONG. 5+ or CEO+CFO = CLUSTER_ELITE. SEC Form 4 EDGAR. Hold: weeks to months.',
      consolidationBreakout: 'Post-catalyst 4-stage pattern: gap → consolidation (10-35d) → volume dry-up (<50% avg) → breakout (>1.5x vol). Highest probability setup. Long AND short variant.',
      fraudInvestigation:    'Accounting fraud / SEC investigation / prominent short seller report / late 10-K or 10-Q filing. 4 triggers. SHORT primary. LONG secondary (overblown allegations). Detectable via EDGAR 8-K keyword monitor.',
    },
    d3OperatingSchedule: 'Pre-market 4am: world markets + macro. Market hours: all strategies. After-hours: position review. Overnight: global monitoring.',
    selfImprovingLoop: 'Every trade → SignalLog with full reasoning → weekly pattern analysis → monthly weight calibration → quarterly strategic review.',
    circuitBreakers: 'Daily drawdown > 3% = go flat. Single trade loss > 2% = close. VIX > 40 = flat. IB Gateway lost > 5min = close all orders.',
    ownerProfile: 'Full-time job. Part-time trader. Cannot watch screens during market hours. D3 must be fully autonomous. Experienced in all 5 strategies.',
    ibkrDataStack: {
      level1: 'US Securities Bundle ~$10/mo via Client Portal — waived if >$30/mo commissions. Needed for TWS API price feeds.',
      level2: 'NYSE OpenBook ~$3/mo + NASDAQ TotalView ~$3/mo — MANDATORY for real money D3. Full order book depth for block detection + execution quality.',
      darkPoolExecution: 'IBKR SmartRouting routes YOUR orders through 8 dark pools for price improvement — this is execution, not analytics.',
      darkPoolSignals: 'Unusual Whales $50/mo — pre-trade dark pool block detection from OTHER institutions. Different from IBKR routing.',
      note: 'Level 2 is not optional for real capital. Without it D3 is blind to order book depth, iceberg orders, and institutional block positioning.',
    },
  },

  vendorPhilosophy: {
    principle: 'Free APIs only until revenue conditions are met. Finding vendor fit in this phase.',
    currentStack: [
      { name: 'Alpha Vantage', status: 'PRIMARY', cost: 'Free 500/day', note: 'NASDAQ Official Vendor. Redistribution permitted.' },
      { name: 'Finnhub', status: 'SECONDARY', cost: 'Free 60/min', note: 'L13 issue — commercial licence needed before S27A ships to guests.' },
      { name: 'Tiingo', status: 'INTERNAL_ONLY', cost: 'Free 500/day', note: 'Chart fallback when AV quota exhausted.' },
      { name: 'SEC EDGAR', status: 'FREE FOREVER', cost: '$0', note: 'Public domain. Phase C AI Scorecard primary source.' },
      { name: 'Anthropic Claude API', status: 'ACTIVE', cost: '~$0.01-0.03/call', note: 'AI analysis + teasers + D3 gate.' },
      { name: 'SMTP2GO', status: 'ACTIVE', cost: 'Free 1000/mo', note: 'Auth + digest. No npm package.' },
    ],
    fmpStatus: {
      status: 'PAUSED',
      reason: 'Quote $8,925/yr declined March 16 2026 (pre-revenue, zero users).',
      why: 'Clean structured endpoints — attractive for Phase C/D.',
      revisitWhen: 'Ad revenue > $200/mo OR subscription MRR > $500/mo',
      contact: 'neha@financialmodelingprep.com',
    },
    vendorAgnosticRule: 'Never architect any feature to require a specific vendor. Every backend endpoint uses vendor-agnostic naming (/api/fundamentals/:ticker not /api/fmp/:ticker). Swap vendors in one file without touching frontend.',
  },

  portalSegregation: {
    status: 'PHASE D — documented only. No code changes until OCI live and product mature.',
    current: 'Single frontend. Super Admin identified by SUPER_ADMIN_EMAILS env var only.',
    future: [
      { subdomain: 'marketpulze.com',            purpose: 'Public frontend. Guest + Member access.', phase: 'Current — live now' },
      { subdomain: 'admin.marketpulze.com',       purpose: 'Dedicated Admin management portal. Users/Requests/Testers/Settings. Separate auth domain.', phase: 'Phase D' },
      { subdomain: 'superadmin.marketpulze.com',  purpose: 'Personal trading suite. IBKR execution. All /trading/* routes. Super Admin only. Completely isolated from public frontend.', phase: 'Phase D' },
    ],
    trigger: 'OCI live + Phase D personal suite complete + product has real users',
    why: 'Security (separate auth domains, separate servers), performance (trading suite code never loads for guests), clear operational separation, future-proof for team growth.',
  },

  sdlcStrategy: {
    current: {
      model: '2-branch: dev -> main',
      description: 'All work on dev. main auto-deploys to Cloudflare Pages. Never push directly to main.',
    },
    phaseC: {
      model: '3-branch: dev -> test -> main',
      description: 'Test branch runs automated scenario suite before promotion to main. Activates when product skeleton complete.',
    },
    phaseD: {
      model: 'Automated testing agent',
      description: 'Agent runs full SCENARIO_MATRIX before any branch promotion. No manual scenario validation needed.',
    },
    testingLevels: [
      { level: 'Smoke Tests',        current: true,      tool: 'scripts/smoke-test.sh',   count: '418 checks', description: 'File existence + grep checks. Runs before every commit. Non-negotiable.' },
      { level: 'Scenario Tests',     current: 'manual',  tool: 'SCENARIO_MATRIX (sessions.js)', count: '15+ scenarios', description: 'Manual validation today. Agent-automated in Phase C+. Every new feature adds scenarios.' },
      { level: 'Unit Tests',         current: false,     tool: 'Jest (Phase C)',           count: '02cd4d7', description: 'Every scoring function, every API endpoint, every utility function.' },
      { level: 'Integration Tests',  current: false,     tool: 'Jest + Supertest (Phase C)', count: '02cd4d7', description: 'Full user flows: guest -> gate -> sign in -> analysis -> share.' },
      { level: 'System Tests',       current: false,     tool: 'Custom agent (Phase D)',   count: '02cd4d7', description: 'Live API health + performance benchmarks + cross-browser + mobile.' },
    ],
    rules: [
      'Every scenario written during coding must be added to SCENARIO_MATRIX in sessions.js',
      'Every SCENARIO_MATRIX entry must eventually have a corresponding smoke check',
      'Smoke test count goes UP each session, never down',
      'Never promote to main if any smoke check fails',
      'QA Health tab in /roadmap shows current status of all scenarios',
    ],
  },

  buildRule: 'Every session = 1 guest feature + 1 Super Admin equivalent. Never build a guest feature without its personal suite counterpart in the same commit. See HANDOVER Section 26.',
  currentSession: 'SCREENER-FRAG — DELIVERED, NOT YET COMMITTED as of this update. Screener fragmentation (12-file split) + field registry/FilterPanel.jsx + deepened Tier 3 signal capture + live ticker validation + VM-1 first Oracle read pool. Ran ahead of EVAL-SCREENER prereqs by deliberate owner decision. See HEAD.md / HANDOVER.md for full state once apply-SCREENER-FRAG.sh has actually run — this line describes what was delivered this session, not a confirmed live state.',
  lastCommit: 'pending — SCREENER-FRAG not yet run through apply-SCREENER-FRAG.sh as of this update. Prior sessions (SECTOR-MAPPING-MODULAR onward through OPS-BOUNDARY-DOC-SYNC) also still not fully confirmed via git log in materials available to Claude — see HEAD.md for the 9d47777 commit-gap note. Do not treat this value as current without confirming against actual git log after running the apply script.',
  smokeCount: '759/759', // auto-generated by apply-session.sh from a static count of smoke-test.sh's check() calls, run before the functional smoke pass — see CHANGELOG.md for what changed this session. UPDATED (CONGRESS-PAGE-CONTROLS window/status filters + self-check position bug fixed): was 667/667. The self-check-position bug flagged last update is NOW FIXED — a permanent anchor comment ("NEW CHECKS INSERT ABOVE THIS LINE") was added right before the self-check block, and both apply-session-template.sh and hotfix-template.sh now target that anchor instead of "Final Results", so future insertions can never again land after the self-check runs. 675 = mechanical grep count (674 real checks + 1 self-check), verified by actually RUNNING smoke-test.sh against a scratch repo containing the real changed files (not just bash -n) — the 13 new checks for this round confirmed passing for real, not just parsed. Not yet confirmed against the owner's own full real-repo run; the self-check itself will now correctly flag any discrepancy on that run rather than silently matching the wrong number, unlike before this fix.
};

// ─── PRE-SESSION CHECKLIST ────────────────────────────────────────────────────
// Run BEFORE every session. Paste results into opening message.
// Source: HANDOVER.md Section 0 Steps 1–3

export const PRE_SESSION = {
  diagnosticCommands: `
# Paste all output into opening message
echo "=== CHART ===" && curl -s "https://api.marketpulze.com/api/seo/NVDA/chart?resolution=D" | python3 -c "import sys,json; d=json.load(sys.stdin); print('Candles:', len(d.get('candles',[])))"
echo "=== EARNINGS ===" && curl -s "https://api.marketpulze.com/api/seo/NVDA/earnings" | python3 -c "import sys,json; d=json.load(sys.stdin); print('Records:', len(d.get('earnings',[])))"
echo "=== NEWS ===" && curl -s "https://api.marketpulze.com/api/seo/NVDA/news" | python3 -c "import sys,json; d=json.load(sys.stdin); print('Articles:', len(d.get('articles',[])))"
echo "=== CONSENSUS ===" && curl -s "https://api.marketpulze.com/api/seo/NVDA/consensus" | python3 -c "import sys,json; d=json.load(sys.stdin); print('Consensus:', d.get('consensus'))"
echo "=== GIT ===" && cd /workspaces/marketpulze && git log --oneline -3
echo "=== BRANCH ===" && git branch --show-current
echo "=== SMOKE ===" && bash scripts/smoke-test.sh 2>&1 | tail -5`,

  checklist: [
    'Run diagnostics above and paste ALL output into opening message',
    'Confirm git branch is dev (not main) — NEVER work on main',
    'Confirm last 3 commits match Section 13 in HANDOVER.md',
    'Open sessions.js — find session with status:"next", read its prereqs carefully',
    'If prereqs list scenario validation — run those scenarios manually on live site before any code',
    'If prereqs list smoke-test must be green — run bash scripts/smoke-test.sh first',
    'Upload HANDOVER.md + the files listed in the current session\'s "files" array',
    'Do NOT write any code until diagnostics and ALL prereqs are confirmed passing',
  ],

  openingMessageTemplate: `
Continuing MarketPulze development.

Live:      https://marketpulze.com
Backend:   https://api.marketpulze.com
Repo:      github.com/Marketpulze/marketpulze (branch: dev → main)
Codespace: /workspaces/marketpulze

Last completed: Session [N] [✅/⚠️] — [description]
Current:        Session [N+1] — [description]

Uploading: [list every file you are uploading]

Diagnostic results:
  Chart:     Candles: [X]
  Earnings:  Records: [X]
  News:      Articles: [X]
  Consensus: [object or null]
  Branch:    [dev — must not be main]
  Git:       [last 3 commits]
  Smoke:     [X/36 passing]

[If validating prereq scenarios — paste checklist with ✅/❌ per scenario]

Please:
1. Read current session prereqs from sessions.js before anything else
2. Read all uploaded files completely before suggesting any changes
3. Do not write any code until I confirm prereqs and diagnostics are passing
4. Fix one scenario at a time — confirm working on live site before moving to next`,
};

// ─── END-OF-SESSION CHECKLIST ────────────────────────────────────────────────
// Source: HANDOVER.md Section 0 Step 5 + Section 19
// IMPORTANT: smoke-test BEFORE build BEFORE commit — this is the exact order from Rule 4/5

export const END_SESSION = {
  terminalCommands: `
cd /workspaces/marketpulze

# STEP 1 — Smoke test FIRST (must be 100% before build)
bash scripts/smoke-test.sh
# Expected: all checks ✅, zero ❌

# STEP 2 — Build (must be green before commit)
npm run build 2>&1 | tail -5
# Expected: "Compiled successfully" or "webpack compiled"

# STEP 3 — Review status
git status && git log --oneline -3`,

  deployCommand: `
# Full deploy — single chained command (Rule 5 in sessions.js)
cd /workspaces/marketpulze \\
  && bash scripts/smoke-test.sh \\
  && npm run build 2>&1 | tail -5 \\
  && bash tasks/build-verify.sh \\
  && git add -A \\
  && git commit -m "feat/fix/docs: description" \\
  && git push origin dev \\
  && git checkout main && git merge dev \\
  && git push origin main \\
  && bash tasks/post-deploy-check.sh \\
  && git checkout dev`,

  checklist: [
    '[ ] bash scripts/smoke-test.sh — must be 100% passing (ALL checks green)',
    '[ ] npm run build — must compile successfully, zero ESLint errors',
    '[ ] git status — confirm only intended files changed',
    '[ ] Update sessions.js: completed session status → "done", add date + commit hash',
    '[ ] Update sessions.js: next session status → "next"',
    '[ ] Update HANDOVER.md header line (line 4): _Session N ✅ · Date_',
    '[ ] Update HANDOVER.md Section 13: add session row with commit hash',
    '[ ] Update HANDOVER.md Section 14: point to next session (sessions.js id)',
    '[ ] Update HANDOVER.md opening message: Last completed + Next lines',
    '[ ] If new smoke checks added this session: update scripts/smoke-test.sh',
    '[ ] If new files added: update Section 6 (Key File Paths) in HANDOVER.md',
    '[ ] Update SESSION.md: current session scope for next session (BRIEF.md retired April 2026)',
    '[ ] Update Claude Project Instructions: Current Session + Last Commit + add row to Session History',
    '[ ] Update CLAUDE.md: current session line + last commit hash',
    '[ ] Update CHANGELOG.md: add session row (session name, date, commit, what was built)',
    '[ ] Run full deploy command above (smoke → build → build-verify → commit → push dev → merge main → post-deploy-check → back to dev)',
  ],

  commitMessageFormats: [
    'feat: [feature name] — S{N} [session name]',
    'fix: [what was fixed] — S{N}',
    'docs: HANDOVER.md + sessions.js — S{N} session close',
    'refactor: [what was refactored] — no behaviour change',
  ],
};

// ─── SCENARIO TESTING MANIFEST ──────────────────────────────────────────────
// Full user scenario matrix. Update status after every session that touches the feature.
// Status: '✅' passing, '⚠️' partial/flaky, '❌' broken, '🔲' not built yet
// Run 3–5 scenarios manually on live site at START of every session (prereq gate).

export const SCENARIO_MATRIX = {
  stockPage: [
    { id: 'S1',  desc: 'Guest visits /stocks/NVDA — page loads, stats grid shows 12 fields (no blanks)', status: '✅' },
    { id: 'S2',  desc: 'Guest sees teaser + gate + Request Invite + Sign In buttons', status: '✅' },
    { id: 'S3',  desc: 'Guest Sign In → auto-runs analysis after login (no extra click needed)', status: '✅' },
    { id: 'S4',  desc: 'Member pre-run → Run Full Analysis button + "Uses 1 of monthly analyses"', status: '✅' },
    { id: 'S5',  desc: 'Member clicks Run — loading → inline AnalysisResult appears above chart', status: '✅' },
    { id: 'S6',  desc: 'Inline metrics not blank (P/E, 52W High/Low show real values)', status: '✅' },
    { id: 'S7',  desc: 'Score breakdown bars show real numbers (not all same height)', status: '✅' },
    { id: 'S8',  desc: 'Re-run button appears after first run', status: '✅' },
    { id: 'S9',  desc: 'Navigating AAPL → MSFT clears previous analysis (no stale data)', status: '✅' },
    { id: 'S10', desc: '429 limit error → orange warning + Upgrade Plan button', status: '✅' },
    { id: 'S11', desc: 'Chart loads on all 5 tabs (1D/1W/1M/3M/1Y) — not empty', status: '✅' },
    { id: 'S12', desc: 'Earnings panel shows EPS data for NVDA', status: '✅' },
    { id: 'S13', desc: 'News shows NVDA-relevant articles (not generic market news)', status: '✅' },
    { id: 'S14', desc: 'Analyst Consensus shows bars and rating', status: '✅' },
    { id: 'S15', desc: 'Share button creates card, copies URL, navigates to /share/:uuid', status: '✅' },
  ],
  calendar: [
    { id: 'CAL1', desc: '/calendar loads for guest — earnings tab shows current week data', status: '🔲' },
    { id: 'CAL2', desc: 'Week navigation: ← / This / Next / +2 weeks all load correct data', status: '🔲' },
    { id: 'CAL3', desc: 'Economic tab: Fed/CPI/NFP with Impact pill colours — NO AI signals present', status: '🔲' },
    { id: 'CAL4', desc: 'IPO tab shows upcoming IPOs with exchange + price range', status: '🔲' },
    { id: 'CAL5', desc: 'My Watchlist filter: locked for guest, shows tooltip CTA "Request Invite"', status: '🔲' },
    { id: 'CAL6', desc: 'Ticker click → navigates to /stocks/:ticker', status: '🔲' },
    { id: 'CAL7', desc: 'Legal disclaimer visible on all 3 tabs', status: '🔲' },
  ],
  news: [
    { id: 'NEWS1', desc: '/news loads — 20 articles visible with source/time/headline/sentiment pill', status: '🔲' },
    { id: 'NEWS2', desc: 'Category filters work: All / Earnings / Economic / M&A / Macro', status: '🔲' },
    { id: 'NEWS3', desc: 'My Watchlist filter: locked for guest, tooltip visible', status: '🔲' },
    { id: 'NEWS4', desc: 'Trending Tickers sidebar shows most-mentioned tickers', status: '🔲' },
    { id: 'NEWS5', desc: 'Ticker tags are clickable → navigate to /stocks/:ticker', status: '🔲' },
    { id: 'NEWS6', desc: 'Headline opens article in new tab', status: '🔲' },
    { id: 'NEWS7', desc: 'Load more shows additional 20 articles', status: '🔲' },
  ],
  stockPageNews: [
    { id: 'SN1', desc: 'StockPage news tab shows 20 articles (was 5)', status: '🔲' },
    { id: 'SN2', desc: 'Sentiment pill on each article (POSITIVE/NEGATIVE/NEUTRAL)', status: '🔲' },
    { id: 'SN3', desc: 'Excerpt (first ~100 chars) visible under headline', status: '🔲' },
    { id: 'SN4', desc: 'Relative time shows "3h ago" not raw timestamp', status: '🔲' },
    { id: 'SN5', desc: 'Load more button loads additional articles', status: '🔲' },
  ],
  stockPageTabs: [
    { id: 'TAB1', desc: 'Tab navigation visible: Overview | Technicals | News | Insiders | Filings', status: '🔲' },
    { id: 'TAB2', desc: 'Overview tab shows existing content unchanged', status: '🔲' },
    { id: 'TAB3', desc: 'News tab shows S27A enhanced news feed (20 articles, sentiment)', status: '🔲' },
    { id: 'TAB4', desc: 'Technicals / Insiders / Filings tabs show "Coming soon" placeholder', status: '🔲' },
  ],
  screener: [
    { id: 'SCR1',  desc: '/screener loads — seed tickers appear, score rings coloured, signal badges correct (green BULLISH / amber NEUTRAL / red BEARISH)', status: '🔲' },
    { id: 'SCR2',  desc: 'Guest applies BULLISH filter — only BULLISH rows visible, count updates', status: '🔲' },
    { id: 'SCR3',  desc: 'Guest applies Technology sector filter — only tech tickers visible', status: '🔲' },
    { id: 'SCR4',  desc: 'Guest clicks Top BULLISH scan — results sort by score desc, D3 log fires with d3Strategy: B_MOMENTUM', status: '🔲' },
    { id: 'SCR5',  desc: 'Guest clicks Volume Breakouts scan — results sort by volume, D3 log fires with d3Strategy: A_PUMP', status: '🔲' },
    { id: 'SCR6',  desc: 'Guest sees exactly 10 rows then lock row — "X more results hidden · Request Invite" CTA visible', status: '🔲' },
    { id: 'SCR7',  desc: 'Guest clicks CSV export — gate shows, no file downloads, Request Invite CTA visible', status: '🔲' },
    { id: 'SCR8',  desc: 'Guest clicks a ticker row — navigates to /stocks/:ticker', status: '🔲' },
    { id: 'SCR9',  desc: 'Guest Reset button clears all filters — results return to default sort', status: '🔲' },
    { id: 'SCR10', desc: 'Member visits /screener — all results visible, no lock row, CSV export button visible', status: '🔲' },
    { id: 'SCR11', desc: 'Member clicks CSV export — .csv file downloads with all 8 columns', status: '🔲' },
  ],
  // Added June 2026 — first backend/pipeline category in SCENARIO_MATRIX (previously all 6
  // categories were frontend/UX-only). Created specifically to answer: smoke-test.sh's binary
  // pass/fail check() has no way to represent "known gap, not yet built, here's why" without
  // either a fake-always-passing check (misleads automation that trusts pass=verified) or
  // silent deletion (loses the record). SCENARIO_MATRIX already supports exactly this via the
  // '🔲' status — not yet verified, not a failure, a tracked pending item — and this is the
  // mechanism this project already designed for the future automated test agent to read
  // ("Agent runs full SCENARIO_MATRIX before any branch promotion," see sdlcStrategy.phaseD
  // above). Smoke-test.sh stays binary-only (real, executable checks); pipeline-capability gaps
  // that are real but not yet testable belong here instead.
  congressionalPipeline: [
    { id: 'CONG1', desc: 'House cross-chamber amplifier detection (bipartisan + bicameral trade clustering) — removed by SIGNAL-DECOUPLE May 2026, not relocated, not implemented anywhere in current codebase. Closes when congressionalSignalPipeline.js (BACKLOG-22) implements it.', status: '🔲' },
    { id: 'CONG2', desc: 'tickerResolver.js Tier 4 Yahoo fallback — hand-rolled HTTPS call, built and tested against a mocked response only, never run against the live Yahoo endpoint. Closes when a live confirmation run on VM-PIPELINE is done and documented (BACKLOG-23).', status: '🔲' },
    { id: 'CONG3', desc: 'congressionalTransformPipeline.js + congressionalDataQualityPipeline.js — Transform and Enrich stages, never run against real production data, only constructed fixtures. Closes when both run successfully against the validated Senate raw dump and results are reconciled.', status: '🔲' },
    { id: 'CONG4', desc: 'House full-enumeration audit — the same standard applied to Senate (every distinct value, zero sampling, reconciled by exact count) has not yet been applied to House. Closes when that audit completes with results documented in HANDOVER.md.', status: '🔲' },
  ],
};

// ─── SESSIONS ────────────────────────────────────────────────────────────────
//
// FIELDS:
//   id             — unique stable identifier, never changes, used for cross-referencing
//   sessionNumber  — human label (S27A etc). Set '02cd4d7' until officially assigned.
//   name           — display name
//   status         — 'done' | 'next' | 'planned' | 'future'
//   phase          — phase label (must match key in RoadmapPage phaseColor object)
//   date           — date completed (done sessions only)
//   commit         — git commit hash (done sessions only)
//   items          — feature list shown in UI
//   prereqs        — GATE: Claude must confirm ALL of these before writing any code
//   doD            — Definition of Done: criteria for marking this session complete
//   smokeChecks    — bash lines to ADD to scripts/smoke-test.sh when this session ships
//   files          — files to upload to Claude at start of this session
//   vendorDeps     — APIs/vendors this session depends on + their status


// ─── D3 SIGNAL UNIVERSE ──────────────────────────────────────────────────────
export const D3_SIGNAL_UNIVERSE = {

  engineSignals: {
    PROCEED_LONG:  'Uptrend + low VIX + greed + breadth bullish + macro aligned → IBKR buy',
    PROCEED_SHORT: 'Downtrend + high VIX + fear + breadth bearish + macro aligned → IBKR sell short',
    PROCEED_HEDGE: 'Mixed signals + inverted yield + credit stress → inverse ETFs (SH, PSQ, SDS)',
    ABORT:         'VVIX > 100 OR SKEW > 140 OR VIX backwardation OR credit spreads blowing out → no position',
  },

  gateChain: [
    'T1: Regime gate — VIX level, Fear & Greed, Yield curve, Breadth',
    'T2: Trend confirmation — SMA cross, RSI, MACD, Volume',
    'T3: Sentiment confirmation — Put/Call ratio, Short interest, Insider ratio',
    'T4: Macro alignment — DXY, Credit spreads, Copper/Gold ratio',
    'T5: Execution timing — VWAP deviation, Relative volume, Pre-market gap',
    'ALL gates must pass → PROCEED signal. ANY gate fail → ABORT or HOLD',
  ],

  tiers: [
    {
      tier: 1,
      name: 'Market Regime',
      signals: [
        { name: 'Fear & Greed',        source: 'Computed (VIX+S&P+breadth)', status: 'LIVE',    session: 'S27B' },
        { name: 'VIX level + label',   source: 'VIXY ETF (Finnhub)',         status: 'LIVE',    session: 'S27B' },
        { name: 'Yield curve 1Y-30Y',  source: 'SHY/IEI/IEF/TLT ETFs',      status: 'LIVE',    session: 'S27B' },
        { name: '10Y-2Y Spread',       source: 'Computed from yields',        status: 'LIVE',    session: 'S27B' },
        { name: 'Market breadth',      source: 'Computed from movers',        status: 'LIVE',    session: 'S27B' },
        { name: 'VVIX',                source: 'Finnhub free',                status: 'PLANNED', session: 'S27B-ext' },
        { name: 'VIX term structure',  source: 'VIX3M vs VIXY',              status: 'PLANNED', session: 'S27B-ext' },
        { name: 'SKEW Index',          source: 'CBOE free API',               status: 'PLANNED', session: 'S27C' },
      ],
    },
    {
      tier: 2,
      name: 'Commodities & FX',
      signals: [
        { name: 'Oil (USO)',           source: 'Finnhub free',  status: 'LIVE',    session: 'S27B' },
        { name: 'Gold (GLD)',          source: 'Finnhub free',  status: 'LIVE',    session: 'S27B' },
        { name: 'Silver (SLV)',        source: 'Finnhub free',  status: 'LIVE',    session: 'S27B' },
        { name: 'Copper (CPER)',       source: 'Finnhub free',  status: 'LIVE',    session: 'S27B' },
        { name: 'USD Index (UUP)',     source: 'Finnhub free',  status: 'LIVE',    session: 'S27B' },
        { name: 'Gold/Silver ratio',   source: 'Computed',      status: 'LIVE',    session: 'S27B' },
        { name: 'Copper/Gold ratio',   source: 'Computed',      status: 'LIVE',    session: 'S27B' },
        { name: 'Oil/Gold ratio',      source: 'Computed',      status: 'PLANNED', session: 'S27C' },
      ],
    },
    {
      tier: 3,
      name: 'Credit & Risk',
      signals: [
        { name: 'Credit spreads HYG/LQD', source: 'Finnhub free',      status: 'LIVE',    session: 'S27B' },
        { name: 'Put/Call ratio',          source: 'CBOE free API',     status: 'PLANNED', session: 'S27C' },
        { name: 'Short interest %',        source: 'Quiver ~$50/mo',    status: 'PLANNED', session: 'S27D' },
        { name: 'Insider buy/sell ratio',  source: 'SEC EDGAR free',    status: 'PLANNED', session: 'S27D' },
        { name: 'Congressional trades',    source: 'Quiver ~$50/mo',    status: 'PLANNED', session: 'S27D' },
        { name: 'Dark pool % volume',      source: 'FINRA TRF paid',    status: 'FUTURE',  session: 'Phase C' },
      ],
    },
    {
      tier: 4,
      name: 'Momentum & Trend',
      signals: [
        { name: '50/200 SMA cross',     source: 'Existing chart data', status: 'PLANNED', session: 'S27C' },
        { name: 'RSI divergence',       source: 'Existing chart data', status: 'PLANNED', session: 'S27C' },
        { name: 'MACD histogram',       source: 'Existing chart data', status: 'PLANNED', session: 'S27C' },
        { name: 'Volume surge',         source: 'Existing data',       status: 'PLANNED', session: 'S27C' },
        { name: 'ATH proximity',        source: 'Existing data',       status: 'PLANNED', session: 'S27C' },
        { name: 'Advance/Decline line', source: 'Computed',            status: 'PLANNED', session: 'S27D' },
      ],
    },
    {
      tier: 5,
      name: 'Execution Intelligence (D3 only)',
      signals: [
        { name: 'Gamma exposure (GEX)', source: 'SpotGamma paid',  status: 'FUTURE', session: 'D3a' },
        { name: 'Order flow imbalance', source: 'IBKR native',     status: 'FUTURE', session: 'D3a' },
        { name: 'VWAP deviation',       source: 'Computed',        status: 'FUTURE', session: 'D3a' },
        { name: 'Relative volume',      source: 'Existing data',   status: 'FUTURE', session: 'D3b' },
        { name: 'Pre-market gap+vol',   source: 'Existing data',   status: 'FUTURE', session: 'D3b' },
      ],
    },
  ],

  tradingDirections: {
    note: 'D3 engine is BIDIRECTIONAL — designed to profit in all market conditions',
    long:  { trigger: 'Uptrend + VIX < 20 + F&G > 50 + breadth bullish + macro aligned',  instruments: 'IBKR direct buy',          engineSignal: 'PROCEED_LONG'  },
    short: { trigger: 'Downtrend + VIX > 25 + F&G < 40 + breadth bearish + macro aligned', instruments: 'IBKR sell short',          engineSignal: 'PROCEED_SHORT' },
    hedge: { trigger: 'Mixed + inverted curve + credit stress',                              instruments: 'SH / PSQ / SDS inverse ETFs', engineSignal: 'PROCEED_HEDGE' },
    abort: { trigger: 'VVIX > 100 OR SKEW > 140 OR VIX backwardation',                     instruments: 'Cash / no position',       engineSignal: 'ABORT'         },
  },

  legalBoundary: 'Personal use only. Single IBKR account. Single owner. Every trade logged with full Claude reasoning chain — never purged. No RIA registration needed.',
};


// ─── D3 SIGNAL CHAIN — PERMANENT ARCHITECTURAL PRINCIPLE ─────────────────────
// Established April 2026. Read every session before writing any code.
// RULE: Every feature must answer three questions before build:
//   (1) What signal does this generate? (MongoDB model + fields)
//   (2) Which D3 layer consumes it and via which endpoint?
//   (3) What does it add to D3 conviction or risk reduction?

export const D3_SIGNAL_CHAIN = {
  principle: "The website IS D3's brain. Every feature = signal collection for the D3 conviction engine.",
  rule: 'Before any code: state signal generated, D3 layer consuming it, conviction added. Cannot be skipped.',

  phaseA: {
    DrawingLog:          { signal: 'crowd_support_zones + crowd_resistance_zones', layer: 'L3', value: 'crowd-validated price levels — multiple members drawing same level = stronger signal' },
    ScreenerSignalLog:   { signal: 'sector_attention_spike', layer: 'L1', value: 'sector focus signal — 3x 30-day avg over 3 days = sector regime input' },
    WatchlistLog:        { signal: 'demand_flag', layer: 'L1+L2', value: 'ticker demand accumulation — 2x 30-day avg in 48hr raises L2 threshold to 0.70' },
    ChartInteractionLog: { signal: 'psychological_levels', layer: 'L3', value: 'member hover clusters = price level attention → informs exit zone selection' },
    CalculatorUsage:     { signal: 'calculator_conviction', layer: 'L3', value: 'long-term member belief in ticker — DCA commitment signal' },
  },

  phaseB: {
    GuestStockViews:   { signal: 'anon_demand', layer: 'L1', value: 'anonymous attention before institutional moves — pre-institutional demand detection' },
    S27H_Indicators:   { signal: 'member_indicator_readings', layer: 'L2', value: 'Bollinger/MACD/VWAP member-validated readings → pattern confirmation input' },
    S27_ALGO:          { signal: 'genuine_bearish_signal', layer: 'L1+L2', value: 'FOUNDATIONAL — broken algorithm = zero D3 signal variance. Algorithm V2 enables genuine BEARISH scores = contrarian conviction signals. Signal variance IS the D3 value.' },
    S27I_Fibonacci:    { signal: 'fib_levels', layer: 'L3', value: 'HIGHEST VALUE SIGNAL — crowd fib levels = crowd-validated institutional targets. Multiple members same price = strongest signal.' },
    PriceAlerts:       { signal: 'level_conviction', layer: 'L1', value: 'member price conviction at specific level → demand amplifier' },
    W52HighScan:       { signal: 'scenario_k_feed', layer: 'L1', value: 'direct Scenario K Volatility Regime Rotation feed' },
  },

  phaseC: {
    AIScorecard:       { signal: 'quality_score', layer: 'L1', value: 'Quality factor filter — ALL tickers must pass before event-driven entry eligible' },
    AnalystEstimates:  { signal: 'revision_signal', layer: 'L1+L3', value: 'Scenario J earnings revision momentum inputs' },
    InsiderDisplay:    { signal: 'cluster_signal', layer: 'L3', value: 'Scenario G/L Form 4 cluster feed' },
    CongressionalData: { signal: 'legislative_signal', layer: 'L3', value: 'Scenario L convergence — congressional + insider simultaneous' },
    AIScanner:         { signal: 'strategy_sophistication', layer: 'L6', value: 'member scan complexity → D3 signal weight calibration input' },
  },

  aggregationAPI: {
    endpoint: 'GET /api/d3/signals/:ticker',
    buildSession: 'OCI-8',
    purpose: 'Single D3-consumable JSON per ticker — all Tier 3 signals aggregated',
    schema: ['crowd_support_zones', 'crowd_resistance_zones', 'psychological_levels', 'demand_flag', 'sector_attention_spike', 'fib_levels', 'calculator_conviction', 'quality_score', 'momentum_score'],
  },

  d3Consumption: {
    L1: ['sector_attention_spike', 'demand_flag', 'price_vs_52w_high_pct', 'volume_dry_up_streak', 'days_to_earnings', 'quality_score', 'momentum_score'],
    L2: ['demand_flag (threshold modifier: 0.75→0.70)', 'member_indicator_readings'],
    L3: ['crowd_support_zones', 'crowd_resistance_zones', 'psychological_levels', 'fib_levels', 'calculator_conviction', 'EDGAR Form 4 insider cluster', 'quality_score'],
    L5: ['portfolio_state', 'PDT_counter', 'cooling_off_registry', 'sector_concentration', 'all tier signals as confirmation context'],
    L6: ['all trades + signal API data at trade time + actual outcomes → strategy_weights + threshold_adjustments + proposed_filters'],
  },
};


// ─── VENDOR REGISTRY ─────────────────────────────────────────────────────────
// Centralised status of all vendors + infrastructure decisions.
// Update status here when decisions change. Displayed in RoadmapPage Architecture tab.
// status values: 'ACTIVE' | 'PLANNED' | 'EVALUATING' | 'PAUSED' | 'BANNED' | 'BLOCKED' | 'LAST_RESORT'

// ─── VENDOR REGISTRY ────────────────────────────────────────────────────────
export const VENDOR_REGISTRY = [
  // ── DATA PROVIDERS ───────────────────────────────────────────────────────
  {
    name: 'Alpha Vantage',
    status: 'ACTIVE',
    category: 'data',
    note: 'NASDAQ Official Vendor — redistribution permitted on paid plans. FREE tier: 25 req/day GLOBAL_QUOTE (NOT 500) + 500/day NEWS_SENTIMENT (different endpoint). ToS: free tier = personal use only. Paid Plan30 $49.99/mo = commercial display permitted. Use free for D3 internal signals. Upgrade to Plan30 at Phase C for guest display.',
    pricing: { free: '25/day GLOBAL_QUOTE, 500/day NEWS_SENTIMENT', plan30: '$49.99/mo', plan75: '$74.99/mo', plan150: '$99.99/mo' },
    tosDisplay: 'Free=personal only. Plan30+ = commercial display permitted.',
    tosD3: 'Free tier permitted for D3 internal use.',
  },
  {
    name: 'Finnhub',
    status: 'EVALUATING',
    category: 'data',
    note: 'L13 — free tier ToS = personal/dev only, cannot display to guests/members. Paid $50/mo/market (US = $50/mo). ToS page not publicly accessible — must email contact@finnhub.io to confirm display rights for paid plans. Currently used for: calendar endpoints (earnings/economic/IPO) + ETF intelligence proxies. D3 internal regime signals permitted on free tier. Do not display raw Finnhub data to any user until commercial licence confirmed.',
    pricing: { free: '$0 (60/min, personal only)', us_market: '$50/mo', all_in_one: '$500/mo' },
    tosDisplay: 'Free=L13 violation. Paid=$50/mo — confirm ToS before Phase C display.',
    tosD3: 'Free tier permitted for D3 internal signals.',
    action: 'Email contact@finnhub.io before Phase C: confirm commercial display rights on $50/mo plan.',
  },
  {
    name: 'Tiingo',
    status: 'INTERNAL_ONLY',
    category: 'data',
    note: 'ToS Section 7.3 confirmed: ALL plans (Personal/Power/Corporate) = internal consumption only. Redistribution/display requires special request + additional fees — contact api@tiingo.com. NEVER display to guests or members on any plan. Plans from your account: Personal ~$10/mo, Power ~$30/mo, Corporate ~$50/mo. Redistribution licence = negotiated separately. Current use: chart fallback + ETF seed cache (backend only) = compliant. D3 automated trading on personal account = permitted.',
    pricing: { personal: '~$10/mo (your account)', power: '~$30/mo', corporate: '~$50/mo', redistribution: 'Contact api@tiingo.com' },
    tosDisplay: 'NEVER — all plans internal only. Redistribution requires separate negotiated licence.',
    tosD3: 'Any plan permitted for D3 personal single-account trading.',
  },
  {
    name: 'Marketstack',
    status: 'PLANNED',
    category: 'data',
    note: 'ToS confirmed: display to end users explicitly permitted on all paid plans (users must use for personal reference only). US data sourced from Tiingo but with commercial display wrapper. Basic $8.99/mo = EOD + 10yr history + ETF + commercial use. Professional $43.99/mo adds real-time + commodities. Business $127.99/mo adds company statements + 6 EDGAR endpoints. Platinum Support $719.88/yr is optional add-on. Phase B deep dive session needed before integrating. Evaluate: does Professional plan cover all immediate needs before paying for Business?',
    pricing: { basic: '$8.99/mo ($107.88/yr)', professional: '$43.99/mo ($527.88/yr)', business: '$127.99/mo ($1,535.88/yr)', platinum_support: '$719.88/yr optional add-on' },
    tosDisplay: 'All paid plans — display to end users explicitly permitted in ToS.',
    tosD3: 'Permitted on all paid plans.',
    action: 'Phase B: subscribe Professional $43.99/mo first. Upgrade to Business when Phase C AI Scorecard needs company statements + EDGAR.',
  },
  {
    name: 'SEC EDGAR',
    status: 'ACTIVE',
    category: 'data',
    note: 'US Government public domain. No API key, no registration, no cost, no ToS restrictions. Rate limit 10 req/sec. All filings (10-K/10-Q/8-K/Form4) free forever. Display to anyone, any purpose. Phase C AI Scorecard primary source. EDGAR data is same data Marketstack EDGAR endpoints wrap — use direct for free, use Marketstack for cleaner JSON if subscribed.',
    pricing: { free: '$0 forever' },
    tosDisplay: 'Public domain — no restrictions.',
    tosD3: 'No restrictions.',
  },
  {
    name: 'FMP',
    status: 'NEGOTIATING',
    category: 'data',
    note: 'Full quote $8,925/yr declined. Counter-proposal: Fundamentals ($2K) + Insider ($800) only, development rate $0 beta, commercial at 100 members. Expiry 2026-04-16 — respond before. EDGAR pipelines (OCI-5) replace both at $0 — sign FMP only if EDGAR normalization insufficient.',
    contact: 'Neha Grewal — info@financialmodelingprep.com',
    pricing: { enterprise: '$8,925/yr' },
    tosDisplay: 'Paid plans permit commercial display.',
    tosD3: 'Permitted on paid plans.',
  },
  // ── CONGRESSIONAL + ALTERNATIVE DATA ──────────────────────────────────────
  {
    name: 'House Clerk Direct',
    status: 'ACTIVE',
    category: 'data',
    note: 'US Government source. disclosures-clerk.house.gov/FinancialDisclosure. Raw STOCK Act XML/PDF filings — all House members. Free forever, public domain, no restrictions. Requires parsing: XML extraction, ticker mapping, amendment handling. This is the PRIMARY source — all vendors (Quiver, UW, PTT) parse this same data. Use for D3 congressional signal T3 gate. Senate equivalent: efts.senate.gov.',
    pricing: { free: '$0 forever' },
    tosDisplay: 'Public domain — no restrictions.',
    tosD3: 'No restrictions. Primary source for D3 T3 congressional signal.',
    action: 'Register free — no signup needed. HTTP GET requests only. Add user-agent header per SEC policy.',
  },
  {
    name: 'PoliticianTradeTracker',
    status: 'EVALUATING',
    category: 'data',
    note: 'politiciantradetracker.us — Small Southern California startup. Parses STOCK Act disclosures. Free tier, no credit card. Congressional stock trades parsed as clean JSON. ToS not fully confirmed for commercial display — contact before Phase C member display. Good for D3 internal development + testing. Reliability risk: new startup, no SLA.',
    pricing: { free: '$0 (limited rate)', paid: 'Not publicly disclosed' },
    tosDisplay: 'Unconfirmed — contact before Phase C display.',
    tosD3: 'Personal use permitted.',
    action: 'Register free tier for D3 development. Confirm commercial ToS before Phase C.',
  },
  {
    name: 'AInvest API',
    status: 'EVALUATING',
    category: 'data',
    note: 'docs.ainvest.com — Financial data platform with congressional trades endpoint (/open/ownership/congress). Also covers institutional ownership, analyst ratings, earnings. ToS explicitly states: commercial use requires written approval from AInvest. Free tier available, no credit card. Cannot display to users without written commercial agreement. D3 internal signals permitted on free tier.',
    pricing: { free: 'Free tier (limited calls/day)', paid: 'Contact for commercial agreement' },
    tosDisplay: 'Written approval required for commercial display.',
    tosD3: 'Free tier permitted for D3 personal use.',
    action: 'Register free for D3 signal development. Email for commercial approval before Phase C display.',
  },
  {
    name: 'Quiver Quantitative',
    status: 'PLANNED',
    category: 'data',
    note: 'quiverquant.com — ToS confirmed: website = personal use only, BUT personal securities trading explicitly exempt from commercial use restriction. API has separate licence. Hobbyist $10/mo = Tier 1 (congressional + insider + gov contracts + lobbying). Trader $75/mo = + short interest + WSB sentiment + Wikipedia views. API licence likely covers member display — confirm before Phase C. Use for Phase C member display of congressional/insider signals.',
    pricing: { hobbyist: '$10/mo (Tier 1)', trader: '$75/mo (Tier 1+2)', institution: 'Custom' },
    tosDisplay: 'API licence required. Hobbyist $10/mo likely covers member display — confirm.',
    tosD3: 'Personal securities trading explicitly exempt from ToS commercial restriction.',
    action: 'Subscribe Hobbyist $10/mo at Phase C start. Confirm API display rights before building member UI.',
  },
  {
    name: 'Unusual Whales',
    status: 'PLANNED',
    category: 'data',
    note: 'unusualwhales.com — $50/mo covers 100+ endpoints: congressional trades, insider flow, options flow, dark pool blocks, ETF inflows, institutional holdings, Put/Call ratio, Greek exposure. The most comprehensive alternative data platform at this price. Redistribution licence requires enterprise contact. Key for D3: dark pool block detection (T3) + options flow (T4) + congressional (T3). Cannot use IBKR for dark pool ANALYTICS (IBKR routes through dark pools for execution only — different function). Use Unusual Whales for pre-trade signals.',
    pricing: { standard: '$50/mo', historical_options: '$250/mo additional', enterprise: 'Contact for redistribution' },
    tosDisplay: 'Confirm display rights before Phase C member UI. Enterprise for redistribution.',
    tosD3: '$50/mo plan covers D3 internal signal use.',
    action: 'Subscribe at D3a start (paper trading). Confirm display rights for potential Phase C member dark pool signal tab.',
  },
  {
    name: 'CoinGecko',
    status: 'PLANNED',
    category: 'data',
    note: 'Phase C crypto tab only. Demo (free) = testing only, NOT commercial display. Analyst $129/mo = commercial display permitted. ToS: display to users with "Data provided by CoinGecko" attribution. Cannot redistribute API access itself. Phase C crypto tab needs Analyst $129/mo for guest/member display. Prices + % only — NO signals ever (L6 crypto signal violation).',
    pricing: { demo: '$0 testing only', analyst: '$129/mo commercial display', pro: 'Custom' },
    tosDisplay: 'Demo=testing only. Analyst $129/mo = commercial display with attribution.',
    tosD3: 'Not applicable — D3 is equities only.',
    action: 'Register Demo free for development. Upgrade to Analyst $129/mo only when Phase C crypto tab ships.',
  },
  {
    name: 'Yahoo Finance',
    status: 'BANNED',
    category: 'data',
    note: 'Personal use only (L3). Never display to users. Never redistribute. Unofficial scrapers violate ToS.',
    tosDisplay: 'BANNED.',
    tosD3: 'Not recommended — ToS risk.',
  },
  {
    name: 'Stooq',
    status: 'INTERNAL_ONLY',
    category: 'data',
    note: 'Free historical data downloads. Personal/research use only. Store in OCI ATP (not MongoDB — too large). 506MB US daily, 181MB world indices, 0.9MB macro. Use for D3 backtesting only. Never display to users.',
    pricing: { free: '$0 — download only' },
    tosDisplay: 'NEVER — personal/research only.',
    tosD3: 'Permitted for backtesting on OCI ATP.',
  },
  {
    name: 'Polygon.io',
    status: 'BANNED',
    category: 'data',
    note: 'Permanently banned. ToS violation (L4). Never re-add for any reason.',
  },
  // ── AI ────────────────────────────────────────────────────────────────────
  {
    name: 'Anthropic Claude API',
    status: 'ACTIVE',
    category: 'ai',
    note: 'Usage-based, commercial use fully permitted. claude-sonnet-4-6 for AI analysis + MD7 brief + D3 gate. ~$0.003/brief, ~$0.008/analysis. Budget: ~$1/mo now, ~$20/mo at Phase C scale. Switch to Haiku 4.5 for simple classification to reduce cost 3x.',
    pricing: { haiku: '$1/$5 per MTok', sonnet: '$3/$15 per MTok', opus: '$5/$25 per MTok' },
    tosDisplay: 'Commercial use fully permitted.',
    tosD3: 'Fully permitted. D3 gate uses Claude for final trade decision.',
  },
  // ── INFRASTRUCTURE ────────────────────────────────────────────────────────
  { name: 'GoDaddy Domain',        status: 'ACTIVE',      category: 'infrastructure', note: 'marketpulze.com — 10-year registration. DNS managed by Cloudflare nameservers. Never let lapse.' },
  { name: 'GoDaddy Outlook Email', status: 'PLANNED',     category: 'infrastructure', note: 'Microsoft 365 Business email via GoDaddy. support@marketpulze.com → Outlook-hosted. ~$72/yr.' },
  { name: 'Cloudflare Pages',      status: 'ACTIVE',      category: 'infrastructure', note: 'Frontend CDN. Free forever. Auto-deploy from GitHub main.' },
  { name: 'Railway',               status: 'CANCELLED',   category: 'infrastructure', note: 'Decommissioned May 4, 2026. Backend migrated to OCI VM-1 (api.marketpulze.com). No cost. OCI-10 session in sessions-pipeline.js marked DONE.' },
  { name: 'MongoDB Atlas M0',      status: 'ACTIVE',      category: 'infrastructure', note: 'Free tier. Upgrade to M2 $9/mo only when storage > 400MB.' },
  { name: 'SMTP2GO',               status: 'ACTIVE',      category: 'email',          note: '1000 emails/mo free — auth + digest. No npm package.' },
  {
    name: 'OCI Always Free VM',
    status: 'ACTIVE',
    category: 'infrastructure',
    note: 'OCI VM-1 (api.marketpulze.com) LIVE ✅ May 2026. VM-PIPELINE provisioned ✅. Dev VM ✅. ATP-1 MP_REFERENCE + ATP-2 MP_ARCHIVE provisioned. OCI NoSQL 3 tables reserved for stooq. Block storage 200GB on VM-PIPELINE. Bastion provisioned.',
  },
  // ── TRADING + D3 EXECUTION ────────────────────────────────────────────────
  {
    name: 'IBKR Pro',
    status: 'ACTIVE',
    category: 'trading',
    note: 'Account already held. D3 execution layer. IB Gateway port 4001 on OCI VM (after OCI resolves). SmartRouting routes orders through 8 dark pools automatically for price improvement — this is EXECUTION routing, not dark pool data/analytics. For pre-trade dark pool SIGNALS use Unusual Whales (separate product). API: TWS API (Python ib_insync) or Client Portal API. Requires Market Data API Acknowledgement form in Client Portal before API data access.',
    pricing: { commissions: 'Per trade only — no monthly fee for Pro account' },
    tosD3: 'Fully permitted — single account, personal use, no RIA needed.',
  },
  {
    name: 'IBKR Level 1 Market Data',
    status: 'PLANNED',
    category: 'trading',
    note: 'Required for D3 price feeds via TWS API. US Securities Snapshot + Futures Value Bundle: ~$10/mo non-professional. WAIVED if monthly commissions exceed subscription cost. Free non-consolidated streaming (Cboe One + IEX) included with Pro account but not NBBO consolidated. Subscribe via Client Portal → Market Data Subscriptions before D3a.',
    pricing: { us_bundle: '~$10/mo non-professional (waived if >$30/mo commissions)', free_streaming: 'Cboe One + IEX included free (non-consolidated)' },
    tosD3: 'Required for D3 API price feeds.',
    action: 'Subscribe at D3-Infra session. Complete Market Data API Acknowledgement form first.',
  },
  {
    name: 'IBKR Level 2 Market Data',
    status: 'PLANNED',
    category: 'trading',
    note: 'MANDATORY for D3 real money trading. Full order book depth — required for: block trade detection in order book, optimal entry/exit within spread, iceberg order detection, price level support/resistance from order flow, execution quality on large orders. Without Level 2 = operating blind on execution. NYSE OpenBook: ~$3/mo non-professional. NASDAQ TotalView: ~$3/mo non-professional. Subscribe both for US equities coverage. API access via TWS reqMktDepth() function.',
    pricing: { nyse_openbook: '~$3/mo non-professional', nasdaq_totalview: '~$3/mo non-professional', total: '~$6/mo for both' },
    tosD3: 'Mandatory for D3b+ live trading. Subscribe at D3a paper trading phase to build familiarity.',
    action: 'Subscribe both at D3a start. Required before D3b live capital deployment.',
  },
  {
    name: 'Alpaca',
    status: 'PLANNED',
    category: 'trading',
    note: 'Free paper trading. D3b paper trading phase only. Add env vars ONLY at D3a start — not before.',
    tosD3: 'Permitted for paper trading.',
  },
  // ── DSQUARE CORPORATE INFRASTRUCTURE ─────────────────────────────────────
  {
    id: 'dsquare-site',
    sessionNumber: 'S27C-infra',
    name: 'Dsquare Corporate Site — dsquaresgroup.com',
    status: 'done',
    phase: 'Phase B',
    date: 'March 2026',
    commit: 'n/a — separate repo dsquaresgroup/dsquaresgroup-site',
    items: [
      'Corporate site live at dsquaresgroup.com — Cloudflare Pages',
      'GitHub org: github.com/dsquaresgroup — separate from Marketpulze account',
      'Repo: dsquaresgroup/dsquaresgroup-site — private, single index.html',
      'DNS: dsquaresgroup.com moved from GoDaddy NS to Cloudflare (shubhi + vicente NS)',
      'DSQUARE LLC legal name confirmed from IRS S-Corp election letter (May 7 2025)',
      'Public brand: Dsquare (no suffix in nav). Legal: DSQUARE LLC in footer only.',
      'Logo: mirrored D mark — D + mirror(D) = D² — founder + daughter personal meaning',
      'Two D shapes facing each other (lens form) — gold gradient + ghost — trademark-ready geometric',
      'Constellation mark also designed: central hub + 4 satellite nodes (one lit = MarketPulze)',
      'Portfolio: MarketPulze (In Development · Early Beta) + Dsquare Trade (Planned) + Dsquare Enterprise (Planned)',
      'Contact: info@dsquaresgroup.com (group) + info@marketpulze.com (product)',
      'Trademark: MarketPulze™ and Dsquare™ as common law claims (™ not ® — no USPTO registration yet)',
      '™ correct without registration — ® requires USPTO filing',
      'Microsoft 365 Email Essentials: ~$2/mo GoDaddy exclusive bundle (corrected from Business Basic ~$6/mo)',
      'No personal name anywhere on site — no conflict with fulltime employment',
      'No location shown — standard for holding companies (Berkshire, Alphabet do not show address)',
      'Oracle expertise strip: group capability not consulting ad — no individual attribution',
      'GitHub org future team plan: separate teams per product, overlap members possible',
      'marketpulze repo transfer to dsquaresgroup org: AFTER Oracle call confirmed (no disruption risk)',
    ],
    prereqs: [],
    doD: [
      'dsquaresgroup.com loads correctly',
      'DNS on Cloudflare nameservers',
      'Cloudflare Pages custom domain connected',
      'All three portfolio cards show correct status',
      'No personal name or address visible',
      'Contact emails correct: info@dsquaresgroup.com + info@marketpulze.com',
    ],
    smokeChecks: [],
    files: ['dsquaresgroup-site/index.html'],
    vendorDeps: [],
  },
];


// ─── SESSION DATA — assembled from 4 source files ───────────────────────────
//
// ARCHITECTURE (permanent — Sessions Architecture session April 2026):
//   sessions-archive.js  → ARCHIVE_SESSIONS  (done, frozen)
//   sessions-active.js   → ACTIVE_SESSIONS   (current 2-3 month horizon)
//   sessions-pipeline.js → PIPELINE_SESSIONS (approved + sequenced, waiting)
//   sessions-future.js   → FUTURE_SESSIONS   (Phase C/D vision, unscheduled)
//
// NEVER edit SESSIONS here. Edit the correct source file.
// NEVER import source files directly in components — always use sessions.js.
// ─────────────────────────────────────────────────────────────────────────────
export const SESSIONS = [
  ...ARCHIVE_SESSIONS,
  ...ACTIVE_SESSIONS,
  ...PIPELINE_SESSIONS,
  ...FUTURE_SESSIONS,
];

export const getSessionById     = (id) => SESSIONS.find(s => s.id === id);
export const getCurrentSession  = ()   => SESSIONS.find(s => s.status === 'next');
export const getDoneSessions    = ()   => SESSIONS.filter(s => s.status === 'done');

export const SESSION_PROGRESS = {
  done:    SESSIONS.filter(s => s.status === 'done').length,
  total:   SESSIONS.length,
  current: getCurrentSession()?.sessionNumber || 'None',
  currentName: getCurrentSession()?.name || '',
};
